/**
 * Sample bank — loads real recorded drum + piano samples from a free CDN
 * (tonejs.github.io/audio, CORS-enabled, cached by the browser).
 *
 * First call to loadDrumKit() takes ~1-3 seconds to fetch + decode ~6 mp3 files.
 * After that, in-memory cache means all subsequent renders are <100 ms.
 *
 * Cache keys are kit names (e.g. "CR78", "acoustic-kit"). Returned shape:
 *   { kick: AudioBuffer, snare: AudioBuffer, hihat: AudioBuffer,
 *     tom1: AudioBuffer, tom2: AudioBuffer, tom3: AudioBuffer }
 *
 * Piano: real Steinway grand from Yamaha Salamander C5 Grand. Sampled every
 * 3 semitones across 8 octaves (A0..C8). We pitch-shift the nearest sample
 * to render arbitrary MIDI notes.
 */

import { loadSynthDrumKit, isSynthDrumKit, SYNTH_DRUM_KITS, renderKitWithParams } from "./synthDrumKits.js";
import { findCustomKit, isCustomKitId } from "./customDrumKits.js";
import { BASS_MACRO_SCHEMA, BASS_MACRO_DEFAULTS, clampBassMacros } from "./bassMacros.js";
import { findCustomBass, isCustomBassId } from "./customBassVoices.js";

// Re-exports so existing imports of these from sampleBank still work.
export { BASS_MACRO_SCHEMA, BASS_MACRO_DEFAULTS };

const TONE_DRUM_BASE = "https://tonejs.github.io/audio/drum-samples";
const TONE_PIANO_BASE = "https://tonejs.github.io/audio/salamander";
// Brosowsky's hosted Tone.js instrument library (CORS-enabled, free).
const INSTRUMENT_BASE = "https://nbrosowsky.github.io/tonejs-instruments/samples";

// All available drum kits the app can load. The CDN-backed kits live under
// the Tone.js audio repo and follow the same 6-file schema (kick / snare /
// hihat / tom1 / tom2 / tom3). The "synth-*" entries are rendered in-memory
// by lib/synthDrumKits.js so they load instantly and work offline.
export const DRUM_KITS_AVAILABLE = [
  // Sampled (CDN)
  "acoustic-kit", "CR78", "R8", "LINN", "Stark", "Techno",
  "breakbeat9", "breakbeat8", "breakbeat13", "KPR77", "Kit3", "Kit8",
  "4OP-FM", "TheCheebacabra1", "TheCheebacabra2", "Bongos",
  // Synthesised (in-memory, instant)
  ...SYNTH_DRUM_KITS,
];

/** Rich catalog used by the Drum Machine kit picker. Each entry:
 *    { id, label, group: "sampled" | "synth", desc }
 *  `id` matches a DRUM_KITS_AVAILABLE entry and is what gets passed to
 *  `loadDrumKit`. Order here drives the order in the UI dropdown. */
export const DRUM_KITS = [
  // ── Sampled (real recordings, Tone.js CDN) ────────────────────────────
  { id: "acoustic-kit",     label: "Acoustic Studio",   group: "sampled", desc: "Real recorded acoustic drum kit" },
  { id: "Stark",            label: "Stark Rock",        group: "sampled", desc: "Aggressive modern rock drums" },
  { id: "LINN",             label: "LinnDrum",          group: "sampled", desc: "Classic 80s sampled drums (Prince, MJ)" },
  { id: "CR78",             label: "Roland CR-78",      group: "sampled", desc: "Iconic 1978 preset rhythm machine" },
  { id: "R8",               label: "Roland R-8",        group: "sampled", desc: "1989 digital drum machine" },
  { id: "KPR77",            label: "Korg KPR-77",       group: "sampled", desc: "1982 analog drum machine" },
  { id: "4OP-FM",           label: "FM Synth Kit",      group: "sampled", desc: "4-OP FM Yamaha-style drums" },
  { id: "Techno",           label: "Techno Warehouse",  group: "sampled", desc: "Hard techno kicks + claps" },
  { id: "breakbeat8",       label: "Breakbeat · Funk",  group: "sampled", desc: "Funk breakbeat sample pack" },
  { id: "breakbeat9",       label: "Breakbeat · Hip-Hop", group: "sampled", desc: "Old-school hip-hop break" },
  { id: "breakbeat13",      label: "Breakbeat · Vintage", group: "sampled", desc: "Vintage drum break" },
  { id: "Kit3",             label: "Studio Kit 3",      group: "sampled", desc: "General-purpose studio kit" },
  { id: "Kit8",             label: "Studio Kit 8",      group: "sampled", desc: "General-purpose studio kit" },
  { id: "TheCheebacabra1",  label: "Cheebacabra 1",     group: "sampled", desc: "Lo-fi indie kit" },
  { id: "TheCheebacabra2",  label: "Cheebacabra 2",     group: "sampled", desc: "Lo-fi indie kit (variant)" },
  { id: "Bongos",           label: "Latin Bongos",      group: "sampled", desc: "Hand bongos + percussion" },
  // ── Synthesised (in-memory, instant, offline-safe) ────────────────────
  { id: "synth-808",        label: "TR-808 Trap",       group: "synth",   desc: "Sub kick · 808 snare · tight hat" },
  { id: "synth-909",        label: "TR-909 House",      group: "synth",   desc: "Punchy kick · clap · open hat" },
  { id: "synth-lofi",       label: "Lo-Fi Boom-Bap",    group: "synth",   desc: "Filtered drums · vinyl warmth" },
  { id: "synth-dnb",        label: "DnB / Jungle",      group: "synth",   desc: "Hard kick · bright snare · fast hat" },
  { id: "synth-industrial", label: "Industrial",        group: "synth",   desc: "Distorted square kick · metallic" },
  { id: "synth-disco",      label: "Vintage Disco",     group: "synth",   desc: "Four-on-the-floor warm disco" },
];

/** Pretty label for a kit id (falls back to the raw id).
 *  Handles built-ins via the DRUM_KITS catalog AND custom user kits via
 *  the customDrumKits storage lookup. */
export function kitLabel(kitId) {
  const entry = DRUM_KITS.find((k) => k.id === kitId);
  if (entry) return entry.label;
  if (isCustomKitId(kitId)) {
    const custom = findCustomKit(kitId);
    if (custom) return custom.label;
  }
  return kitId;
}

const DRUM_FILES = ["kick", "snare", "hihat", "tom1", "tom2", "tom3"];

// Promise cache so concurrent callers all share the same fetch.
const drumKitCache = new Map();
let pianoPromise = null;

/** Fetch + decode a single mp3 URL into an AudioBuffer. */
async function fetchSample(ctx, url) {
  const r = await fetch(url, { cache: "force-cache" });
  if (!r.ok) throw new Error(`Sample fetch failed: ${url} (${r.status})`);
  const arrBuf = await r.arrayBuffer();
  return await ctx.decodeAudioData(arrBuf);
}

/**
 * Load a full drum kit (6 samples). Promise is cached so a second call for
 * the same kit returns instantly with the same buffers.
 *
 * Dispatch:
 *   - kit ids starting with "custom-"  → user-saved kit, render via params (no network)
 *   - kit ids starting with "synth-"   → built-in synth kit, render in-memory
 *   - other ids                        → fetch from the Tone.js CDN
 */
export function loadDrumKit(ctx, kitName = "acoustic-kit") {
  if (isCustomKitId(kitName)) {
    const entry = findCustomKit(kitName);
    if (entry) return renderKitWithParams(ctx, entry.params, kitName);
    // Fall through to default if the custom kit was deleted between selection
    // and render — graceful degradation.
    return loadSynthDrumKit(ctx, "synth-808");
  }
  if (isSynthDrumKit(kitName)) return loadSynthDrumKit(ctx, kitName);

  const safeKit = DRUM_KITS_AVAILABLE.includes(kitName) ? kitName : "acoustic-kit";
  if (drumKitCache.has(safeKit)) return drumKitCache.get(safeKit);

  const promise = (async () => {
    const entries = await Promise.all(
      DRUM_FILES.map(async (name) => {
        try {
          const buf = await fetchSample(ctx, `${TONE_DRUM_BASE}/${safeKit}/${name}.mp3`);
          return [name, buf];
        } catch (e) {
          console.warn(`[sampleBank] Failed to load ${safeKit}/${name}: ${e.message}`);
          return [name, null];
        }
      })
    );
    return Object.fromEntries(entries);
  })();

  drumKitCache.set(safeKit, promise);
  return promise;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Piano (Salamander Grand) — real recorded notes, sampled every 3 semitones.
//  We name files like A0.mp3, C1.mp3, Ds1.mp3, Fs1.mp3, A1.mp3, ...
//  MIDI numbers (A0=21, C8=108) for fast nearest-neighbour lookup.
// ─────────────────────────────────────────────────────────────────────────────

const PIANO_FILES = [
  ["A0",  21], ["C1",  24], ["Ds1", 27], ["Fs1", 30],
  ["A1",  33], ["C2",  36], ["Ds2", 39], ["Fs2", 42],
  ["A2",  45], ["C3",  48], ["Ds3", 51], ["Fs3", 54],
  ["A3",  57], ["C4",  60], ["Ds4", 63], ["Fs4", 66],
  ["A4",  69], ["C5",  72], ["Ds5", 75], ["Fs5", 78],
  ["A5",  81], ["C6",  84], ["Ds6", 87], ["Fs6", 90],
  ["A6",  93], ["C7",  96], ["Ds7", 99], ["Fs7", 102],
  ["A7", 105], ["C8", 108],
];

/** Load the entire Salamander piano sample set. Resolves to an array of
 *  { midi, buf } sorted ascending. ~3 MB total, cached forever. */
export function loadPiano(ctx) {
  if (pianoPromise) return pianoPromise;

  pianoPromise = (async () => {
    const entries = await Promise.all(
      PIANO_FILES.map(async ([name, midi]) => {
        try {
          const buf = await fetchSample(ctx, `${TONE_PIANO_BASE}/${name}.mp3`);
          return { midi, buf, name };
        } catch (e) {
          console.warn(`[sampleBank] Piano ${name} failed: ${e.message}`);
          return { midi, buf: null, name };
        }
      })
    );
    return entries.filter((e) => e.buf).sort((a, b) => a.midi - b.midi);
  })();

  return pianoPromise;
}

/** Find the closest sampled piano note to a target MIDI value.
 *  Kept for backwards compatibility — `nearestSample` works for all banks. */
export function nearestPianoSample(bank, targetMidi) {
  return nearestSample(bank, targetMidi);
}

// ─────────────────────────────────────────────────────────────────────────────
//  Multi-instrument loader (electric bass, electric guitar, organ, nylon guitar)
//  Each instrument is a sparse set of recorded notes; we pick the nearest one
//  and pitch-shift it via writePitchedSample(). Same pattern as the piano.
// ─────────────────────────────────────────────────────────────────────────────

// Map sample filename (e.g. "As2.mp3") → MIDI number. A4 = 69.
// Note name → semitone offset from C (e.g. "Cs"=1, "Ds"=3, "Fs"=6, "Gs"=8, "As"=10)
const NOTE_NAME_OFFSET = { C: 0, Cs: 1, D: 2, Ds: 3, E: 4, F: 5, Fs: 6, G: 7, Gs: 8, A: 9, As: 10, B: 11 };
function fileNameToMidi(name) {
  // Strip .mp3, parse "<letter><sharp?><octave>" pattern
  const base = name.replace(/\.mp3$/i, "");
  const m = base.match(/^([A-G]s?)(\d)$/);
  if (!m) return null;
  const offset = NOTE_NAME_OFFSET[m[1]];
  if (offset == null) return null;
  const octave = parseInt(m[2], 10);
  // Tone.js convention: C4 = midi 60 (Yamaha). Octave 0 means MIDI 12.
  return (octave + 1) * 12 + offset;
}

// Hand-curated lists (avoids per-load network roundtrips for each instrument's directory).
// Source: github.com/nbrosowsky/tonejs-instruments/samples/* — verified Feb 2026.
const INSTRUMENT_SAMPLES = {
  "bass-electric": [
    "As1", "As2", "As3", "As4", "Cs1", "Cs2", "Cs3", "Cs4", "Cs5",
    "E1", "E2", "E3", "E4", "G1", "G2", "G3", "G4",
  ],
  // Acoustic upright / double bass (real recorded samples from nbrosowsky).
  // Sparse coverage — pitch-shifted by writePitchedSample() for in-between notes.
  "contrabass": [
    "A2", "As1", "B3", "C2", "Cs3", "D2", "E2", "E3", "Fs1", "Fs2", "Gs2", "Gs3",
  ],
  "guitar-electric": [
    "A2", "A3", "A4", "A5", "C3", "C4", "C5", "C6",
    "Cs2", "Ds3", "Ds4", "Ds5", "E2", "Fs2", "Fs3", "Fs4", "Fs5",
  ],
  "organ": [
    "A1", "A2", "A3", "A4", "A5", "C1", "C2", "C3", "C4", "C5", "C6",
    "Ds1", "Ds2", "Ds3", "Ds4", "Ds5", "Fs1", "Fs2", "Fs3", "Fs4", "Fs5",
  ],
  "guitar-nylon": [
    "A2", "A3", "A4", "A5", "B1", "B2", "B3", "B4", "B5",
    "C3", "C4", "C5", "C6", "Cs1", "Cs2", "Cs3", "Cs4", "Cs5",
    "D1", "D2", "D3", "D4", "D5", "E1", "E2", "E3", "E4", "E5",
    "F1", "F2", "F3", "F4", "F5", "G1", "G2", "G3", "G4", "G5",
  ],
};

const instrumentCache = new Map();

/** Load all samples for an instrument. Returns a sorted array of {midi, buf, name}. */
export function loadInstrument(ctx, instrumentName) {
  if (instrumentCache.has(instrumentName)) return instrumentCache.get(instrumentName);
  const list = INSTRUMENT_SAMPLES[instrumentName];
  if (!list) return Promise.resolve([]);

  const promise = (async () => {
    const entries = await Promise.all(
      list.map(async (name) => {
        const midi = fileNameToMidi(name + ".mp3");
        if (midi == null) return null;
        try {
          const buf = await fetchSample(ctx, `${INSTRUMENT_BASE}/${instrumentName}/${name}.mp3`);
          return { midi, buf, name };
        } catch (e) {
          console.warn(`[sampleBank] ${instrumentName}/${name} failed: ${e.message}`);
          return null;
        }
      })
    );
    return entries.filter(Boolean).sort((a, b) => a.midi - b.midi);
  })();

  instrumentCache.set(instrumentName, promise);
  return promise;
}

/** Find the closest sampled note for any instrument bank. */
export function nearestSample(bank, targetMidi) {
  if (!bank || bank.length === 0) return null;
  let best = bank[0];
  let bestDist = Math.abs(targetMidi - best.midi);
  for (let i = 1; i < bank.length; i++) {
    const d = Math.abs(targetMidi - bank[i].midi);
    if (d < bestDist) { bestDist = d; best = bank[i]; }
  }
  return best;
}

/**
 * Mix an AudioBuffer into an output buffer at `startSample`, optionally
 * pitch-shifted by `semitones` (positive = up, negative = down) using linear
 * interpolation resampling. Stereo-preserving; respects pan + gain.
 *
 * Used both for drum hits (semitones=0, gain=velocity) and pitched piano
 * notes (semitones derived from nearest-sample distance).
 */
export function writePitchedSample(outBuf, startSample, srcBuf, semitones = 0, opts = {}) {
  if (!srcBuf) return;
  const { gain = 1, pan = 0, durationLimit = null } = opts;
  const L = outBuf.getChannelData(0);
  const R = outBuf.numberOfChannels > 1 ? outBuf.getChannelData(1) : null;
  const sL = srcBuf.getChannelData(0);
  const sR = srcBuf.numberOfChannels > 1 ? srcBuf.getChannelData(1) : sL;
  const srcLen = srcBuf.length;

  const ratio = Math.pow(2, semitones / 12);
  // Output sample count = source length compressed by the pitch ratio.
  let outLen = Math.floor(srcLen / ratio);
  if (durationLimit) {
    const maxOut = Math.floor(durationLimit * outBuf.sampleRate);
    outLen = Math.min(outLen, maxOut);
  }

  const panL = Math.cos((pan + 1) * Math.PI / 4) * 1.41;
  const panR = Math.sin((pan + 1) * Math.PI / 4) * 1.41;

  for (let i = 0; i < outLen; i++) {
    const out = startSample + i;
    if (out < 0) continue;
    if (out >= L.length) break;
    const srcIdx = i * ratio;
    const i0 = Math.floor(srcIdx);
    const i1 = Math.min(i0 + 1, srcLen - 1);
    const frac = srcIdx - i0;
    const vL = sL[i0] * (1 - frac) + sL[i1] * frac;
    const vR = sR[i0] * (1 - frac) + sR[i1] * frac;
    L[out] += vL * gain * panL;
    if (R) R[out] += vR * gain * panR;
  }
}

/** Sum the bytes of every cached sample — diagnostic only. */
export function getCacheStats() {
  return {
    drumKits: Array.from(drumKitCache.keys()),
    pianoLoaded: !!pianoPromise,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
//  Synthesised bass voices (no network — rendered in-memory at first request).
//  Each voice exposes the same { midi, buf, name }[] shape so they're a
//  drop-in replacement for sample-based banks in the Bass Player.
// ─────────────────────────────────────────────────────────────────────────────

/** MIDI → frequency. A4 (69) = 440 Hz. */
function midiToHz(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Render a synth bass note into a freshly-allocated AudioBuffer.
 *  voice: "synth" | "sub" | "slap" | "reese" | "acid" | "moog" | "fm" | "pluck" | "wobble"
 *  macros: BASS_MACRO_SCHEMA shape — applied uniformly on top of the voice's
 *          built-in DSP. Defaults to a no-op (no audible change vs. legacy).
 *  Returns a 2-channel buffer ~0.85-1.2 s long, ready to be pitch-shifted further. */
function renderSynthBassNote(ctx, midi, voice, macros) {
  const m = clampBassMacros(macros);
  // Pitch macro shifts the requested MIDI by ±2 octaves.
  const effectiveMidi = midi + m.pitch;
  const sr = ctx.sampleRate;
  // Voice-specific durations — wobble + reese need more time so the LFO/growl
  // gets at least 2 cycles to read as "wobbly" rather than a single dip.
  const dur = voice === "sub" ? 1.0
            : voice === "wobble" ? 1.2
            : voice === "pluck" ? 0.55
            : 0.85;
  const len = Math.floor(dur * sr);
  const buf = ctx.createBuffer(2, len, sr);
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);
  const freq = midiToHz(effectiveMidi);
  const w = 2 * Math.PI * freq;

  // Per-voice ADSR shape.
  let att, dec, sus, rel;
  switch (voice) {
    case "sub":    att = 0.020; dec = 0.6;  sus = 0.55; rel = 0.35; break;
    case "reese":  att = 0.008; dec = 0.5;  sus = 0.45; rel = 0.20; break;
    case "slap":   att = 0.003; dec = 0.25; sus = 0.18; rel = 0.20; break;
    case "acid":   att = 0.002; dec = 0.18; sus = 0.30; rel = 0.10; break;  // tight TB-303 pluck
    case "moog":   att = 0.005; dec = 0.30; sus = 0.65; rel = 0.25; break;  // long sustain (held bass)
    case "fm":     att = 0.001; dec = 0.20; sus = 0.40; rel = 0.20; break;  // bell-fast attack
    case "pluck":  att = 0.001; dec = 0.18; sus = 0.05; rel = 0.10; break;  // pluck — near zero sustain
    case "wobble": att = 0.020; dec = 0.4;  sus = 0.60; rel = 0.20; break;  // sustains so LFO is heard
    default:       att = 0.008; dec = 0.25; sus = 0.18; rel = 0.20;         // "synth"
  }
  // iter-141 — apply decay macro: scales decay AND release proportionally so
  // the envelope keeps a consistent shape (just stretched/compressed in time).
  dec *= m.decay;
  rel *= m.decay;

  const detune = voice === "sub" ? 0 : 0.997;
  const wL = w;
  const wR = w * (voice === "sub" ? 1 : (2 - detune));

  const reeseDetuneA = 1.0;
  const reeseDetuneB = 1.005;

  // Lowpass smoothing state per channel (single-pole IIR for "synth" and a
  // sharper resonant filter for "acid" / "wobble").
  let prevL = 0, prevR = 0;
  // Resonant state-variable filter accumulators (only used by acid/wobble).
  // y_lp and y_bp track lowpass + bandpass through the SVF iteration so the
  // resonance peak is correct without an O(N²) reset every sample.
  let svfLpL = 0, svfBpL = 0, svfLpR = 0, svfBpR = 0;

  for (let i = 0; i < len; i++) {
    const t = i / sr;
    // ADSR envelope
    let env;
    if (t < att) env = t / att;
    else if (t < att + dec) env = 1 - (1 - sus) * ((t - att) / dec);
    else if (t < dur - rel) env = sus;
    else env = sus * Math.max(0, 1 - (t - (dur - rel)) / rel);

    let sL, sR;
    if (voice === "sub") {
      // Pure sine + a small 2nd harmonic for body (808-like).
      sL = Math.sin(wL * t) * 0.95 + Math.sin(2 * wL * t) * 0.08;
      sR = sL;
    } else if (voice === "slap") {
      const saw = 2 * (t * freq - Math.floor(0.5 + t * freq));
      const click = (i < 0.012 * sr) ? (Math.random() * 2 - 1) * (1 - i / (0.012 * sr)) : 0;
      sL = saw * 0.6 + click * 0.4;
      const sawR = 2 * (t * freq * detune - Math.floor(0.5 + t * freq * detune));
      sR = sawR * 0.6 + click * 0.4;
    } else if (voice === "reese") {
      const sawA = 2 * (t * freq * reeseDetuneA - Math.floor(0.5 + t * freq * reeseDetuneA));
      const sawB = 2 * (t * freq * reeseDetuneB - Math.floor(0.5 + t * freq * reeseDetuneB));
      sL = (sawA + sawB) * 0.5;
      const sawC = 2 * (t * freq * 0.997 - Math.floor(0.5 + t * freq * 0.997));
      sR = (sawB + sawC) * 0.5;
    } else if (voice === "acid") {
      // TB-303 — square + resonant lowpass with high envelope-mod amount.
      // The classic "squelch" comes from a tight env modulating a high-Q LPF.
      const sq = Math.sign(Math.sin(w * t)) * 0.6;
      const saw = 2 * (t * freq - Math.floor(0.5 + t * freq)) * 0.5;
      sL = sq + saw;
      const sqR = Math.sign(Math.sin(wR * t)) * 0.6;
      const sawR = 2 * (t * freq * detune - Math.floor(0.5 + t * freq * detune)) * 0.5;
      sR = sqR + sawR;
    } else if (voice === "moog") {
      // Warm analog square + sub-octave saw for that thick Moog low end.
      const sq = Math.sign(Math.sin(w * t)) * 0.55;
      const sub = Math.sign(Math.sin(0.5 * w * t)) * 0.25;
      sL = sq + sub;
      const sqR = Math.sign(Math.sin(wR * t)) * 0.55;
      sR = sqR + sub;
    } else if (voice === "fm") {
      // 2-op FM: modulator's freq = carrier × 1 (integer ratio for clean tone),
      // index ramps down with envelope giving a bell-like spectrum that
      // settles into a sine — classic DX7 "Bass 1" character.
      const modIndex = 4 + env * 8;
      const mod = Math.sin(w * t) * modIndex;
      sL = Math.sin(w * t + mod);
      sR = Math.sin(wR * t + mod);
    } else if (voice === "pluck") {
      // Short pluck — triangle wave with very fast envelope already handled
      // above (sus=0.05). Slight 2nd-harmonic for tonal definition.
      const tri = 2 * Math.abs(2 * (t * freq - Math.floor(0.5 + t * freq))) - 1;
      const second = Math.sin(2 * w * t) * 0.2;
      sL = tri * 0.75 + second;
      const triR = 2 * Math.abs(2 * (t * freq * detune - Math.floor(0.5 + t * freq * detune))) - 1;
      sR = triR * 0.75 + second;
    } else if (voice === "wobble") {
      // Dubstep wobble — fat saw + LFO-modulated cutoff. LFO ~ 4 Hz (a wide
      // wobble that reads as syncopated at most tempos). Resonance is high so
      // the cutoff sweep is audible.
      const saw = 2 * (t * freq - Math.floor(0.5 + t * freq));
      const sub = Math.sin(0.5 * w * t) * 0.35; // sub-oct sine for low end
      sL = saw * 0.7 + sub;
      const sawR = 2 * (t * freq * detune - Math.floor(0.5 + t * freq * detune));
      sR = sawR * 0.7 + sub;
    } else {
      // Default "synth" voice — saw + triangle blend
      const saw = 2 * (t * freq - Math.floor(0.5 + t * freq));
      const tri = 2 * Math.abs(2 * (t * freq - Math.floor(0.5 + t * freq))) - 1;
      sL = saw * 0.7 + tri * 0.3;
      const sawR = 2 * (t * freq * detune - Math.floor(0.5 + t * freq * detune));
      const triR = 2 * Math.abs(2 * (t * freq * detune - Math.floor(0.5 + t * freq * detune))) - 1;
      sR = sawR * 0.7 + triR * 0.3;
    }

    // ─── Filter stage ────────────────────────────────────────────────
    if (voice === "acid") {
      // High-Q resonant lowpass — cutoff modulated heavily by env (3× → 18× fundamental).
      const cutoff = freq * (3 + env * 15);
      const q = 0.85;  // resonance
      const f = 2 * Math.sin(Math.PI * cutoff / sr);
      svfLpL = svfLpL + f * svfBpL;
      svfBpL = svfBpL + f * (sL - svfLpL - q * svfBpL);
      svfLpR = svfLpR + f * svfBpR;
      svfBpR = svfBpR + f * (sR - svfLpR - q * svfBpR);
      sL = svfLpL;
      sR = svfLpR;
    } else if (voice === "wobble") {
      // 4 Hz LFO sweeps cutoff between freq×2 and freq×10.
      const lfo = (Math.sin(2 * Math.PI * 4 * t) + 1) * 0.5; // 0..1
      const cutoff = freq * (2 + lfo * 8);
      const q = 0.75;
      const f = 2 * Math.sin(Math.PI * cutoff / sr);
      svfLpL = svfLpL + f * svfBpL;
      svfBpL = svfBpL + f * (sL - svfLpL - q * svfBpL);
      svfLpR = svfLpR + f * svfBpR;
      svfBpR = svfBpR + f * (sR - svfLpR - q * svfBpR);
      sL = svfLpL;
      sR = svfLpR;
    } else if (voice === "moog") {
      // Smooth one-pole LPF that opens slightly with env — gives that
      // "playing the filter" feel.
      const cutoff = freq * (4 + env * 4);
      const rc = 1 / (2 * Math.PI * cutoff);
      const alpha = (1 / sr) / (rc + 1 / sr);
      prevL = prevL + alpha * (sL - prevL);
      prevR = prevR + alpha * (sR - prevR);
      sL = prevL;
      sR = prevR;
    } else if (voice !== "sub" && voice !== "fm") {
      // Original synth/slap/reese/pluck path: one-pole LPF that sweeps with env.
      const cutoff = freq * (3 + env * 5); // 3x..8x fundamental
      const rc = 1 / (2 * Math.PI * cutoff);
      const alpha = (1 / sr) / (rc + 1 / sr);
      prevL = prevL + alpha * (sL - prevL);
      prevR = prevR + alpha * (sR - prevR);
      sL = prevL;
      sR = prevR;
    }
    // "sub" and "fm" skip the filter — their oscillator already produces the
    // right spectral content.

    // iter-141 — sub-mix macro adds a sub-octave sine layered on top of the
    // voice's natural output. Cheap to compute (one sin call) and gives the
    // user a way to thicken any voice with low-end content.
    if (m.subMix > 0) {
      const sub = Math.sin(2 * Math.PI * (freq * 0.5) * t) * m.subMix * 0.4;
      sL += sub;
      sR += sub;
    }

    // iter-141 — drive macro applies a final tanh-style saturation when > 1.
    // Lets users add grit to clean voices (fm, pluck) and dial-in dirt on
    // already-driven voices (industrial-ish character on slap, etc.).
    if (m.drive > 1) {
      const d = m.drive;
      sL = (sL * d) / (1 + Math.abs(sL * d));
      sR = (sR * d) / (1 + Math.abs(sR * d));
    } else if (m.drive < 1) {
      // drive < 1 cleans up the signal (attenuate amplitude pre-final write)
      sL *= m.drive;
      sR *= m.drive;
    }

    L[i] = sL * env * 0.85;
    R[i] = sR * env * 0.85;
  }

  // Soft-limit acid/wobble — their resonant filter peaks slightly over unity
  // (boost at the cutoff). Apply a gentle tanh-style saturation to keep them
  // under 0 dBFS without losing the resonance character.
  if (voice === "acid" || voice === "wobble") {
    for (let i = 0; i < len; i++) {
      L[i] = L[i] / (1 + Math.abs(L[i]) * 0.4);
      R[i] = R[i] / (1 + Math.abs(R[i]) * 0.4);
    }
  }
  return buf;
}

// Cache the rendered synth banks per AudioContext-sampleRate so we don't
// re-render the same 12 notes on every preview/render call.
const synthBassCache = new Map();
const SYNTH_BASS_MIDIS = [24, 26, 28, 29, 31, 33, 35, 36, 38, 40, 41, 43, 45, 47, 48, 50, 52];

/** Returns a {midi, buf, name}[] bank for a synth bass voice.
 *  Voices: built-in synth voice id ("bass-synth"/"bass-sub"/... or just the
 *  voice base "synth"/"sub"/...). Optional `macros` overrides the default
 *  envelope/drive/sub-mix/pitch for custom user presets. The cache key
 *  includes a hash of the macros so two custom presets that share a
 *  basedOn don't collide.
 *  Optional `cacheKey` lets the caller force a unique slot (used by custom
 *  presets to avoid the built-in default macros sharing the same slot). */
export function loadSynthBass(ctx, voiceName, macros, cacheKey) {
  const key = cacheKey || `${voiceName}@${ctx.sampleRate}`;
  if (synthBassCache.has(key)) return synthBassCache.get(key);

  const voice = voiceName.replace(/^bass-/, "");
  const promise = (async () => {
    return SYNTH_BASS_MIDIS.map((midi) => ({
      midi,
      buf: renderSynthBassNote(ctx, midi, voice, macros),
      name: `synth-${voice}-${midi}`,
    }));
  })();
  synthBassCache.set(key, promise);
  return promise;
}

/** Invalidate cached bass renders that match a prefix (used when a custom
 *  preset's macros change so the next play hears the new params). */
export function invalidateBassCache(cacheKeyPrefix) {
  for (const k of synthBassCache.keys()) {
    if (k.startsWith(cacheKeyPrefix)) synthBassCache.delete(k);
  }
}

/** Unified bass loader — accepts either a sample-based name (bass-electric,
 *  contrabass) or a synth voice name (bass-synth, bass-sub, bass-slap,
 *  bass-reese, bass-acid, bass-moog, bass-fm, bass-pluck, bass-wobble).
 *  Always returns a sorted {midi, buf, name}[] bank. */
const SYNTH_BASS_IDS = new Set([
  "bass-synth", "bass-sub", "bass-slap", "bass-reese",
  "bass-acid", "bass-moog", "bass-fm", "bass-pluck", "bass-wobble",
]);
export function loadBassVoice(ctx, voiceName) {
  // iter-141 — custom user presets are stored as `custom-bass-*` ids. Look
  // up the entry and render with its macros against the base voice.
  if (isCustomBassId(voiceName)) {
    const entry = findCustomBass(voiceName);
    if (entry) {
      return loadSynthBass(ctx, entry.basedOn, entry.macros, `${voiceName}@${ctx.sampleRate}`);
    }
    // Custom bass was deleted between selection + load — fall back.
    return loadSynthBass(ctx, "bass-synth");
  }
  if (SYNTH_BASS_IDS.has(voiceName)) return loadSynthBass(ctx, voiceName);
  return loadInstrument(ctx, voiceName);
}

// Public catalog used by the Bass Player UI. iter-139 added acid/moog/fm/
// pluck/wobble for genre coverage (TB-303 acid house, Moog rock, DX7 funk,
// pop synth-pluck, dubstep wobble).
export const BASS_VOICES = [
  { id: "bass-electric", label: "Electric Bass",     group: "sampled",     desc: "Picked finger-style electric bass (real recording)" },
  { id: "contrabass",    label: "Upright (Acoustic)", group: "sampled",    desc: "Acoustic double bass / contrabass (real recording)" },
  { id: "bass-synth",    label: "Synth Bass",        group: "synthesised", desc: "Classic saw + triangle synth bass with filter sweep" },
  { id: "bass-sub",      label: "Sub Bass (808)",    group: "synthesised", desc: "Deep sine sub-bass — for trap, drill, EDM drops" },
  { id: "bass-slap",     label: "Slap Bass",         group: "synthesised", desc: "Bright saw with click transient — funk / pop" },
  { id: "bass-reese",    label: "Reese Bass",        group: "synthesised", desc: "Detuned-saw growl — for DnB, dubstep, neuro" },
  { id: "bass-acid",     label: "Acid (TB-303)",     group: "synthesised", desc: "Squelchy resonant lowpass — acid house, techno" },
  { id: "bass-moog",     label: "Moog Bass",         group: "synthesised", desc: "Warm analog square + sub-octave — rock, funk, fusion" },
  { id: "bass-fm",       label: "FM Bass (DX7)",     group: "synthesised", desc: "Bell-like FM synthesis — 80s pop, funk, R&B" },
  { id: "bass-pluck",    label: "Pluck Bass",        group: "synthesised", desc: "Short triangle pluck — pop, EDM, future-bass" },
  { id: "bass-wobble",   label: "Wobble Bass",       group: "synthesised", desc: "4 Hz LFO-cutoff sweep — dubstep, riddim" },
];
